import {Box} from '@primer/react'
import {memo, Suspense} from 'react'
import {graphql, type PreloadedQuery, useFragment, usePreloadedQuery} from 'react-relay'
import {useRelayEnvironment} from 'react-relay/hooks'

import {Search} from '../components/search/Search'
import type {ClientSideRelayDataGeneratorViewQuery} from './__generated__/ClientSideRelayDataGeneratorViewQuery.graphql'
import type {ListCurrentViewFragment$key} from './__generated__/ListCurrentViewFragment.graphql'
import {currentViewQuery} from './ClientSideRelayDataGenerator'
import {HeaderLoading} from '../components/list/header/HeaderLoading'
import {useRouteInfo} from '../hooks/use-route-info'
import {Header} from '../components/list/header/Header'
import type {ListQuery$key} from './__generated__/ListQuery.graphql'
import type {LoadSearchQuery} from './shared'
import type {ListRepositoryFragment$key} from './__generated__/ListRepositoryFragment.graphql'
import type {ItemIdentifier} from '@github-ui/issue-viewer/Types'

type ListWithFetchedView = {
  currentViewRef: PreloadedQuery<ClientSideRelayDataGeneratorViewQuery>
  fetchedRepository: ListRepositoryFragment$key | null
  search: ListQuery$key
  loadSearchQuery?: LoadSearchQuery
  onCollapse?: () => void
  onSidePanelNavigate?: (issue: ItemIdentifier) => void
  showSsoBanner?: boolean
}

export type ListProps = Omit<ListWithFetchedView, 'currentViewRef'> & {
  fetchedView: ListCurrentViewFragment$key
  queryFromCustomView?: string | null
  onSidePanelNavigate?: (issue: ItemIdentifier) => void
  showSsoBanner?: boolean
}

export const ListWithFetchedView = ({currentViewRef, ...rest}: ListWithFetchedView) => {
  const relayEnvironment = useRelayEnvironment()
  const currentViewEncapsulated = usePreloadedQuery<ClientSideRelayDataGeneratorViewQuery>(
    currentViewQuery,
    currentViewRef,
  )
  if (!currentViewEncapsulated.node) {
    return null
  }

  // This is used to get the original query, without replacements (such as @today)
  let queryFromCustomView: string | null = null
  const id = currentViewRef.variables.id
  if (id) {
    const viewNode = relayEnvironment.getStore().getSource().get(id)
    if (viewNode && viewNode.query) {
      queryFromCustomView = viewNode.query as string
    }
  }

  return <List fetchedView={currentViewEncapsulated.node} queryFromCustomView={queryFromCustomView} {...rest} />
}

const ListInternal = ({
  fetchedView,
  fetchedRepository,
  search,
  loadSearchQuery,
  queryFromCustomView,
  onCollapse,
  onSidePanelNavigate,
  showSsoBanner,
}: ListProps) => {
  const currentViewData = useFragment(
    graphql`
      fragment ListCurrentViewFragment on Shortcutable {
        ...SearchCurrentViewFragment
        ...HeaderCurrentViewFragment
      }
    `,
    fetchedView,
  )

  const repoData = useFragment<ListRepositoryFragment$key>(
    graphql`
      fragment ListRepositoryFragment on Repository {
        ...SearchRepositoryFragment
        ...HeaderSearchRepositoryFragment
      }
    `,
    fetchedRepository || null,
  )

  const searchData = useFragment(
    graphql`
      fragment ListQuery on Searchable
      @argumentDefinitions(
        query: {type: "String!"}
        first: {type: "Int"}
        labelPageSize: {type: "Int!"}
        skip: {type: "Int", defaultValue: null}
        fetchRepository: {type: "Boolean!"}
      ) {
        ...SearchRootFragment
          @arguments(
            query: $query
            first: $first
            labelPageSize: $labelPageSize
            skip: $skip
            fetchRepository: $fetchRepository
          )
      }
    `,
    search,
  )

  const {itemIdentifier} = useRouteInfo()

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        maxWidth: '100%',
      }}
    >
      {currentViewData && (
        <Suspense fallback={<HeaderLoading />}>
          <Header
            setSafeDocumentTitle={!!itemIdentifier?.number}
            currentViewKey={currentViewData}
            currentRepository={repoData ?? null}
            onCollapse={onCollapse}
          />
        </Suspense>
      )}
      {currentViewData && (
        <Search
          itemIdentifier={itemIdentifier}
          currentViewKey={currentViewData}
          currentRepository={repoData ?? null}
          search={searchData}
          loadSearchQuery={loadSearchQuery}
          queryFromCustomView={queryFromCustomView}
          onSidePanelNavigate={onSidePanelNavigate}
          showSsoBanner={showSsoBanner}
        />
      )}
    </Box>
  )
}

export const List = memo(ListInternal)

try{ ListWithFetchedView.displayName ||= 'ListWithFetchedView' } catch {}
try{ ListInternal.displayName ||= 'ListInternal' } catch {}
try{ List.displayName ||= 'List' } catch {}